import { formatarMoedaDigitacao } from '@/design/formato';

/* mascara-moeda.ts — o comportamento de edição do campo de dinheiro, em
 * funções puras: entra texto e posição do cursor, sai texto e posição do
 * cursor. Nada aqui toca `document`. Quem liga isto num <input> é
 * `campo-moeda.tsx`, e é por essa separação que o caso chato (apagar no meio
 * de "1.250,00") é testável sem navegador.
 *
 * A forma do texto é uma só: vazio, ou inteiro agrupado + vírgula + DUAS
 * casas. O dígito digitado entra à frente da vírgula; os centavos já estão
 * lá desde a primeira tecla (ver `formatarMoedaDigitacao` no core).
 *
 * Duas coisas o <input> não resolve sozinho, e são a razão deste módulo:
 *
 *  1. A VÍRGULA NÃO SE DIGITA, ELA PULA. Como o texto já tem vírgula, teclar
 *     "," inseriria uma segunda. A tecla vira navegação: leva o cursor para
 *     os centavos. O ponto faz o mesmo, porque no teclado numérico é ele que
 *     está debaixo do dedo.
 *  2. APAGAR TEM DE VAZIAR O CAMPO. Se o Backspace fosse o do navegador, ele
 *     comeria a vírgula ou um ponto de milhar e "1.250,00" viraria
 *     "125.000,00" na tecla seguinte. Aqui ele apaga DÍGITO: nos centavos
 *     zera a casa (elas são duas, sempre), na parte inteira remove o dígito,
 *     e ao tirar o último dígito o campo fica VAZIO, que é ausência de dado e
 *     vira null lá em cima. Sem isso não dá para desfazer um valor digitado
 *     por engano sem selecionar tudo.
 */

/** Texto do campo e onde o cursor deve ficar depois da edição. */
export type Edicao = { texto: string; cursor: number };

const CASAS = 2;

const soDigitos = (texto: string): string => texto.replace(/\D/g, '');

/** Posição logo depois do n-ésimo dígito de `texto` (n = 0 → começo). */
function depoisDoDigito(texto: string, n: number): number {
  if (n <= 0) return 0;
  let vistos = 0;
  for (let i = 0; i < texto.length; i++) {
    if (texto[i]! >= '0' && texto[i]! <= '9' && ++vistos === n) return i + 1;
  }
  return texto.length;
}

/**
 * Reaplica a máscara depois de uma mudança do `<input>` (digitação, colagem)
 * MANTENDO o cursor onde a pessoa o deixou.
 *
 * A âncora é a QUANTIDADE DE DÍGITOS antes do cursor, não a posição em
 * caracteres: pontos de milhar aparecem e somem, dígitos não. E é contada
 * dentro do LADO em que o cursor está, porque a mesma contagem descreve tanto
 * a borda esquerda quanto a direita da vírgula.
 */
export function aplicarMascara(bruto: string, cursor: number): Edicao {
  const texto = formatarMoedaDigitacao(bruto);
  if (texto === '') return { texto, cursor: 0 };

  const virgula = texto.indexOf(',');
  const virgulaBruta = bruto.indexOf(',');

  if (virgulaBruta !== -1 && cursor > virgulaBruta) {
    const digitados = soDigitos(bruto.slice(virgulaBruta + 1, cursor)).length;
    return { texto, cursor: virgula + 1 + Math.min(digitados, CASAS) };
  }
  const digitados = soDigitos(bruto.slice(0, cursor)).length;
  return { texto, cursor: depoisDoDigito(texto.slice(0, virgula), digitados) };
}

export type Tecla = {
  tecla: string;
  texto: string;
  inicio: number;
  fim: number;
};

/**
 * Resolve as teclas que o `<input>` não pode resolver sozinho. Devolve `null`
 * quando não é uma delas: aí a tecla segue o caminho normal (o navegador
 * edita o texto e `aplicarMascara` reescreve no `change`).
 */
export function editarPorTecla({ tecla, texto, inicio, fim }: Tecla): Edicao | null {
  if (tecla === ',' || tecla === '.') return irParaCentavos(texto);

  // Seleção: quem apaga é o navegador e a máscara reescreve. Selecionar tudo
  // e apagar tem de continuar esvaziando o campo.
  if (inicio !== fim || texto === '') return null;

  const virgula = texto.indexOf(',');
  if (tecla === 'Backspace') return apagarAtras(texto, virgula, inicio);
  if (tecla === 'Delete') return apagarAFrente(texto, virgula, inicio);
  return null;
}

function irParaCentavos(texto: string): Edicao {
  const alvo = texto === '' ? '0,' + '0'.repeat(CASAS) : texto;
  return { texto: alvo, cursor: alvo.indexOf(',') + 1 };
}

function apagarAtras(texto: string, virgula: number, inicio: number): Edicao | null {
  if (inicio > virgula) {
    // Colado na vírgula, o Backspace atravessa para a parte inteira em vez de
    // comer o separador.
    if (inicio === virgula + 1) return { texto, cursor: virgula };
    return { texto: trocarPorZero(texto, inicio - 1), cursor: inicio - 1 };
  }
  const ordem = soDigitos(texto.slice(0, inicio)).length;
  return removerDigitoInteiro(texto, ordem, ordem - 1);
}

function apagarAFrente(texto: string, virgula: number, inicio: number): Edicao | null {
  // Na borda da vírgula o Delete também atravessa, para os centavos.
  if (inicio === virgula) return { texto, cursor: virgula + 1 };
  if (inicio > virgula) {
    if (inicio >= texto.length) return null;
    return { texto: trocarPorZero(texto, inicio), cursor: inicio + 1 };
  }
  const anteriores = soDigitos(texto.slice(0, inicio)).length;
  return removerDigitoInteiro(texto, anteriores + 1, anteriores);
}

/** Zera uma casa de centavo: elas são duas, sempre, então nada some. */
function trocarPorZero(texto: string, pos: number): string {
  return texto.slice(0, pos) + '0' + texto.slice(pos + 1);
}

/**
 * Remove o dígito de `ordem` (1 = o mais à esquerda) da parte inteira e deixa
 * o cursor depois do dígito de `ancora`. Sem parte inteira o campo inteiro
 * fica vazio: é apagando para trás que se desfaz um valor, e parar num
 * "0,00" que não some seria o campo se recusando a ser esvaziado.
 */
function removerDigitoInteiro(texto: string, ordem: number, ancora: number): Edicao | null {
  const virgula = texto.indexOf(',');
  const inteiro = soDigitos(texto.slice(0, virgula));
  if (ordem < 1 || ordem > inteiro.length) return null;

  const restante = inteiro.slice(0, ordem - 1) + inteiro.slice(ordem);
  if (restante === '') return { texto: '', cursor: 0 };

  const novo = formatarMoedaDigitacao(restante + texto.slice(virgula));
  return { texto: novo, cursor: depoisDoDigito(novo.slice(0, novo.indexOf(',')), ancora) };
}
